import { useCallback, useEffect, useMemo, useState } from 'react';
import { MAX_FILE_SIZE } from 'constants/index';
import { Form, message } from 'components';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAttachmentTypeQuery,
  useCurrentUserQuery,
  usePurchaseOrderByIdQuery,
  useLazyPurchaseRequestEmailTemplateQuery, // Using existing email template API
  useContactPersonReadQuery, // Using the correct contact person API
  useSendPurchaseOrderMutation, // Real send purchase order API
} from 'api';
import { ADMIN_ROUTES } from 'constants/routes';
import { Upload } from '../../../../components';
import { useNotificationApi } from '../../../../hooks/useNotificationApi';

export interface IEmailTemplate {
  content: string;
  subject: string;
}

export const useSendPurchaseOrder = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const notificationApi = useNotificationApi();
  const { handleSuccess, handleError } = useNotificationMessages();
  const { id: purchaseOrderId } = useParams<{ id: string }>();
  const userId = JSON.parse(localStorage.getItem('userId') || '""');

  // State
  const [attachmentType, setAttachmentType] = useState<string | undefined>();
  const [fileList, setFileList] = useState<File[]>([]);
  const [error, setError] = useState<string>('');
  const [attachments, setAttachments] = useState<{ type: string | 'null'; file: File; existing?: boolean }[]>([]);
  const [emailTemplate, setEmailTemplate] = useState<IEmailTemplate>({
    content: '',
    subject: 'Purchase Order',
  });
  const [disabledSubmit, setDisabledSubmit] = useState<boolean>(false);
  const [disabledActions, setDisabledActions] = useState<boolean>(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState<boolean>(false);

  // Queries
  const { data: currentUser } = useCurrentUserQuery({ userId }, { skip: !userId });
  const { data: purchaseOrderData, isLoading: isPurchaseOrderLoading } = usePurchaseOrderByIdQuery({
    id: purchaseOrderId || '',
  });
  const { data: attachmentTypes = [] } = useAttachmentTypeQuery({ entityName: 'PURCHASE_ORDER' });
  const [triggerFetchTemplate] = useLazyPurchaseRequestEmailTemplateQuery();
  const [sendPurchaseOrder] = useSendPurchaseOrderMutation();

  const purchaseOrderDetails = useMemo(() => {
    return purchaseOrderData?.purchaseOrder || {};
  }, [purchaseOrderData]);

  // Get supplier contact persons using the correct API
  const { data: contactPersonsData = [] } = useContactPersonReadQuery(
    { entityId: purchaseOrderDetails?.supplier?.id },
    { skip: !purchaseOrderDetails?.supplier?.id }
  );

  // Transform contact persons data
  const contactPersons = useMemo(() => {
    if (!contactPersonsData || contactPersonsData.length === 0) {
      return [{ label: 'Main Contact', value: 'main-contact' }]; // Fallback
    }

    const transformedPersons = contactPersonsData.map((person: any) => {
      return {
        value: person.id || person.value,
        label: person.fullName || person.label,
        languageId: person?.language?.id, // Ensure this is properly mapped
        contacts: person.contacts || {},
        firstName: person.firstName,
        lastName: person.lastName,
        // Include all original data for debugging
        originalData: person,
      };
    });

    return transformedPersons;
  }, [contactPersonsData]);

  // Initialize form with current user email
  useEffect(() => {
    if (currentUser?.email) {
      form.setFieldValue('from', currentUser.email);
    }
    const formattedAttachments: { type: string; file: File }[] = [];
    if (purchaseOrderData?.purchaseOrder?.attachment) {
      formattedAttachments.push({
        type: purchaseOrderData.purchaseOrder.attachment.attachmentType,
        file: {
          ...purchaseOrderData.purchaseOrder.attachment,
          name: purchaseOrderData.purchaseOrder.attachment.fileName,
        },
      });
    }
    if (purchaseOrderData?.purchaseOrder?.clientEmail) {
      formattedAttachments.push({
        type: purchaseOrderData.purchaseOrder.clientEmail.attachmentType,
        file: {
          ...purchaseOrderData.purchaseOrder.clientEmail,
          name: purchaseOrderData.purchaseOrder.clientEmail.fileName,
        },
      });
    }
    setAttachments(formattedAttachments);
  }, [currentUser, form, purchaseOrderData, purchaseOrderDetails]);

  // Handle attachment state changes
  useEffect(() => {
    setDisabledActions(!fileList?.length);
  }, [fileList, attachmentType]);

  // Load email template based on language
  const getLanguageTemplate = useCallback(
    async (languageId: string) => {
      if (!languageId) {
        return;
      }

      try {
        const { data: emailTemplateResponse } = await triggerFetchTemplate({ languageId });

        const { content, subject } = emailTemplateResponse.data;

        setEmailTemplate({ content, subject: subject || 'Purchase Order' });
        form.setFieldValue('subject', subject || 'Purchase Order');
      } catch (error) {
        console.warn('Failed to load email template:', error);
        // Keep default template
      }
    },
    [form, triggerFetchTemplate]
  );

  const onLanguageChange = useCallback(
    (languageId: string) => {
      getLanguageTemplate(languageId);
    },
    [getLanguageTemplate]
  );

  const handleContactPersonChange = useCallback(
    (value: string) => {
      const selectedPerson = contactPersons.find((person: any) => person.value === value);
      form.setFieldsValue({ contactPersonId: value });

      // If contact person has no email, show warning
      if (selectedPerson && !selectedPerson.contacts?.EMAIL?.length) {
        message.warning('Selected contact person has no email address');
      }

      // If contact person has email, set it by default selected
      if (selectedPerson?.languageId) {
        form.setFieldValue('languageId', selectedPerson.languageId);
        getLanguageTemplate(selectedPerson.languageId);
      }
    },
    [form, contactPersons, getLanguageTemplate]
  );

  const handleEmailTemplateChange = useCallback(
    (value: string, key: 'subject' | 'content') => {
      setEmailTemplate((prev) => ({
        ...prev,
        [key]: value,
      }));
      form.setFieldValue(key, value);
    },
    [form]
  );

  const handleAddAttachment = useCallback(() => {
    if (fileList.length > 0) {
      setAttachments((prev) => [...prev, { type: attachmentType || ('null' as any), file: fileList[0] }]);
      setAttachmentType(undefined);
      setFileList([]);
    }
  }, [fileList, attachmentType]);

  const handleDeleteAttachment = useCallback((index: number) => {
    setAttachments((prev) => {
      const newAttachments = [...prev];
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  }, []);

  const handleFileSizeValidation = useCallback((file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setFileList([]);
      setError('File exceeds the maximum size of 5 MB.');
      return Upload.LIST_IGNORE;
    }

    const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
        setIsPreviewVisible(true);
      };
      reader.readAsDataURL(file);
    }

    setFileList([file]);
    setError('');
    return false;
  }, []);

  const handleCancelAttachment = useCallback(() => {
    setAttachmentType(undefined);
    setFileList([]);
  }, []);

  const onSavePurchaseOrderAttachments = useCallback((selectedAttachments: any[]) => {
    setAttachments((prev) => [...prev, ...selectedAttachments]);
  }, []);

  const handleCancel = useCallback(() => {
    navigate(`${ADMIN_ROUTES.purchaseOrder.view}/${purchaseOrderId}/${ADMIN_ROUTES.purchaseOrder.general}`);
  }, [navigate, purchaseOrderId]);

  const handleSubmit = useCallback(async () => {
    if (!currentUser?.email?.trim()) {
      notificationApi({ type: 'error', message: 'Sender email is required' });
      return;
    }

    if (!emailTemplate?.content) {
      notificationApi({ type: 'error', message: 'Email body is required' });
      return;
    }
    setDisabledSubmit(true);
    const formData = new FormData();
    formData.append('purchaseOrderId', purchaseOrderId as string);
    formData.append('supplierId', purchaseOrderDetails?.supplier?.id);
    formData.append('subject', emailTemplate.subject);
    formData.append('languageId', form.getFieldValue('languageId'));
    formData.append('contactPerson', form.getFieldValue('contactPersonId'));
    formData.append(
      'attachmentType',
      attachments
        .filter(
          (attachment) => attachment.type !== 'PURCHASE_ORDER' && attachment.type !== 'EMAIL' && !attachment.existing
        )
        .map((i) => i.type || 'null')
        .join(',')
    );
    formData.append('email', currentUser.email);
    attachments.forEach((i: any) => {
      if (i.type === 'PURCHASE_ORDER' && !i.existing) {
        formData.append('purchaseOrderAttachmentId', i.file.id);
        formData.append('purchaseOrderFileName', i.file.fileName);
      } else if (i.type === 'EMAIL' && !i.existing) {
        const url = new URL(i.file.files['ATTACHMENTS']);
        const attachmentId = url.searchParams.get('attachmentId');
        formData.append('clientEmailId', attachmentId ?? '');
        formData.append('clientEmailFileName', i.file.fileName);
      } else if (i.file?.createDate) {
        formData.append('attachmentId', i.file.id);
        formData.append('attachmentName', i.file.fileName);
      } else {
        formData.append('files', i.file as any, i.file.name);
      }
    });
    formData.append('emailBody', emailTemplate.content);

    try {
      const response: any = await sendPurchaseOrder({ data: formData, purchaseOrderId: purchaseOrderId });
      if (response?.data?.success) {
        handleSuccess('Purchase Order sent successfully!');
        navigate(`${ADMIN_ROUTES.purchaseOrder.view}/${purchaseOrderId}/${ADMIN_ROUTES.purchaseOrder.general}`);
      } else {
        if (response?.error?.data?.data) {
          handleError(response.error);
        } else {
          notificationApi({ type: 'error', message: response?.error?.data?.message || 'Something went wrong!' });
        }
      }
    } catch (err) {
      handleError(err);
    } finally {
      setDisabledSubmit(false);
    }
  }, [
    currentUser,
    emailTemplate,
    purchaseOrderId,
    purchaseOrderDetails,
    form,
    attachments,
    sendPurchaseOrder,
    navigate,
    handleSuccess,
    handleError,
    notificationApi,
  ]);

  return {
    purchaseOrderId,
    form,
    contactPersons,
    isPurchaseOrderLoading,
    purchaseOrderDetails,
    attachmentTypes,
    currentUser,
    fileList,
    error,
    attachmentType,
    attachments,
    emailTemplate,
    disabledActions,
    previewImage,
    isPreviewVisible,
    setAttachmentType,
    setFileList,
    setIsPreviewVisible,
    handleContactPersonChange,
    onLanguageChange,
    handleEmailTemplateChange,
    handleAddAttachment,
    handleDeleteAttachment,
    handleFileSizeValidation,
    handleCancelAttachment,
    handleCancel,
    handleSubmit,
    disabledSubmit,
    onSavePurchaseOrderAttachments,
  };
};
